import { useState } from 'react';
import { Accordion, CheckBox, Message } from '@iwoca/orion';
import { Trans, t } from '@lingui/macro';
import clsx from 'clsx';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { useFormContext } from 'react-hook-form';
import styles from '../NewSignupFormDE.module.css';
import { TSignupFormDataDE } from '../NewSignupFormDE.types';

export const TermsAndConditions = () => {
  const methods = useFormContext<TSignupFormDataDE>();
  const [showAdditionalTerms, setShowAdditionalTerms] = useState(false);

  const termsError =
    methods.formState.errors.applicant?.privacyPolicyAgreement?.message;

  return (
    <>
      <div className={styles.terms}>
        <p>
          <span className={styles.termsHeading}>
            <Trans>
              We require the customer's consent to carry out a credit check
            </Trans>
          </span>
          <Trans>
            I hereby confirm that iwocas{' '}
            <a
              href="https://www.iwoca.de/compliance/allgemeine-geschaeftsbedingungen"
              target="_blank"
              rel="noreferrer"
            >
              general terms and conditions
            </a>
            , the{' '}
            <a
              href="https://www.iwoca.de/compliance/datenschutzhinweis/"
              target="_blank"
              rel="noreferrer"
            >
              Data protection notice
            </a>{' '}
            and{' '}
            <a
              href="https://www.iwoca.de/compliance/schufa-creditreform-hinweise"
              target="_blank"
              rel="noreferrer"
            >
              note on data transfer to Schufa Holding AG
            </a>{' '}
            have been made available to the customer and that I am authorized to
            expressly agree to these on behalf of the customer.
          </Trans>{' '}
          <Trans>
            I also release iwoca Deutschland GmbH and iwoca Management S.à r.l.
            on behalf of the customer. acting on behalf of iwoca SCSp SICAV-SIF
            from banking secrecy to enable data exchange and processing of the
            loan request.
          </Trans>
        </p>
        <p>
          <ButtonLink
            icon={showAdditionalTerms ? 'angleUp' : 'angleDown'}
            iconPosition="right"
            className={clsx(styles.termsHeading, styles.termsButton)}
            type="button"
            onClick={() => setShowAdditionalTerms(!showAdditionalTerms)}
          >
            <Trans>Why do we need the customer's consent?</Trans>
          </ButtonLink>
          {showAdditionalTerms && (
            <>
              <Accordion
                small
                frame={false}
                className={styles.extraTermAccordion}
                title={t`How do we handle customer data?`}
              >
                <Trans>
                  Data security is a top priority at iwoca. We only pass on
                  customer data to close partners if it is necessary to process
                  the loan application. Accordingly, we will never share
                  customer data with third parties not directly related to the
                  application. More information can be found here.
                </Trans>
              </Accordion>
              <Accordion
                small
                frame={false}
                className={styles.extraTermAccordion}
                title={t`Why do we obtain Schufa information from the managers?`}
              >
                <Trans>
                  We can use the information to speed up our credit analysis.
                  The creditworthiness of your customers is included in the
                  credit analysis, but is only crucial if hard negative
                  characteristics are entered. In plain language this means: A
                  disputed telephone bill from 2016 is not a problem, whereas we
                  have to take a closer look at problems with repaying previous
                  loans. Don't worry: 90% of German Schufa files only have
                  positive entries.
                </Trans>
              </Accordion>
              <Accordion
                small
                frame={false}
                className={styles.extraTermAccordion}
                title={t`Will my customer’s Schufa entry get worse as a result of the information?`}
              >
                <Trans>
                  No, our Schufa query has no influence on the score. Only a
                  'score-neutral' condition request will be made, which will not
                  be visible to other companies.
                </Trans>
              </Accordion>
              <Accordion
                small
                frame={false}
                className={styles.extraTermAccordion}
                title={t`Why do we need the consent of all managers?`}
              >
                <Trans>
                  As a rule, we need the consent of all other managing
                  directors, as we also check personal creditworthiness as part
                  of the credit analysis. In order to keep the process
                  uncomplicated and to be able to process the loan request as
                  quickly as possible, we ask you to obtain the consent of all
                  managing directors. If not everyone agrees, please feel free
                  to contact us.
                </Trans>
              </Accordion>
            </>
          )}
          <Trans>
            If you have any further questions about processing your customers'
            data or would like to know more about Schufa etc., simply send us an
            email to <a href="mailto:support@iwoca.de">support@iwoca.de</a> or
            call 069 9432 3020.
          </Trans>
        </p>
      </div>
      <CheckBox
        wrapperClassName={styles.privacyPolicyCheckbox}
        {...methods.register('applicant.privacyPolicyAgreement')}
        label={t`The customer has read and accepted all terms and conditions.`}
        data-testid="privacy-policy-agreement"
      />
      {termsError && (
        <Message
          className={styles.termsError}
          message={termsError}
          type="error"
        />
      )}
    </>
  );
};
